// The task drawer (spec 06 "Task page"; design spec §6.7 detail drawer; Work OS task-detail.tsx;
// Parity P1 wave 1, hybrid design D1 and §3.1): opens over any Work page from `?task=`, so a link
// to a task is a link to the page it sits on. The drawer is one host of `TaskView` (the full
// page and the Inbox preview are the others): it sizes itself and the view lays its panes out
// in that width.
//
// Width: from 1200 px the page behind stays usable (no scrim), and the drawer never covers the
// last 320 px of the main region, so another card is one click away (`min(1120, main − 320)`).
// Narrower, the drawer is modal over a scrim at `min(960, 100vw − 96)`. Inside, work 340 |
// conversation 300 (`SplitRail`, TASK-DRAWER-CHAT): side by side from a 640 px drawer (1280 with
// the sidebar open), stacked below. It closes on the X, Escape and a click outside.
import { Drawer, toast } from "@companyos/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { TaskView } from "./task-page/task-view";

/** The wide breakpoint of the UI drawer (no scrim from here). */
const WIDE = 1200;
const BOARD_LEFT_CLICKABLE = 320;

/** The drawer's width in px for the window and the main region now. */
export function drawerWidth(viewport: number, main: { width: number } | null): number {
  if (viewport < WIDE || !main) return Math.max(320, Math.min(960, viewport - 96));
  return Math.max(320, Math.min(1120, main.width - BOARD_LEFT_CLICKABLE));
}

function useDrawerWidth(open: boolean): number {
  const measure = () =>
    typeof window === "undefined"
      ? 960
      : drawerWidth(
          window.innerWidth,
          document.getElementById("main")?.getBoundingClientRect() ?? null,
        );
  const [w, setW] = useState(measure);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure on open and on resize.
  useEffect(() => {
    if (!open) return;
    const sync = () => setW(measure());
    sync();
    window.addEventListener("resize", sync);
    // The sidebar's width changes the main region without a window resize.
    const main = document.getElementById("main");
    const ro = main && typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;
    if (main) ro?.observe(main);
    return () => {
      window.removeEventListener("resize", sync);
      ro?.disconnect();
    };
  }, [open]);
  return w;
}

/** Menus, popovers, listboxes, dialogs: a click while one is open only closes it. */
const FLOATING =
  "[role=menu], [role=listbox], [role=dialog]:not([data-slot=drawer-content]), [role=alertdialog], [data-slot=popover-content], [data-slot=dropdown-menu-content]";

/**
 * TASK-DRAWER-CHAT: a click outside the drawer closes it (the board behind, the scrim, the
 * sidebar). Not when the click lands on a task card or row (`data-task-id`: it switches the
 * drawer to that task), in another layer (a menu, a popover, a toast: anything outside the app's
 * root), or closes an open menu; and not while the composer holds an unsent message — the
 * drawer stays and says so, the draft kept. Escape and the X are the Drawer's own.
 */
function useClickOutside(taskId: string | null, onClose: () => void) {
  const close = useRef(onClose);
  close.current = onClose;
  const current = useRef(taskId);
  current.current = taskId;
  const open = taskId !== null;
  useEffect(() => {
    if (!open) return;
    let layerOpen = false;
    const panel = () =>
      document.querySelector("[data-task-host=drawer]")?.closest("[data-slot=drawer-content]");
    const onDown = () => {
      layerOpen = Boolean(document.querySelector(FLOATING));
    };
    const onClick = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Element) || e.button !== 0) return;
      const p = panel();
      if (!p || p.contains(t) || layerOpen) return;
      const scrim = t.closest("[data-slot=drawer-overlay]");
      if (!scrim && !document.getElementById("root")?.contains(t)) return;
      if (t.closest("[data-task-id]")) return;
      if (p.querySelector("[data-slot=composer][data-has-text], [data-slot=comment-files]")) {
        toast.add({
          title: "You have an unsent message",
          description: "Send it, or clear it to close the task.",
        });
        p.querySelector<HTMLElement>("[data-slot=composer] [contenteditable=true]")?.focus();
        return;
      }
      // Anything else that opens a task (a gallery card, a calendar item, a table row) switches
      // the drawer in its own click handler: close only if the task is still the same after it.
      const url = () => new URLSearchParams(window.location.search).get("task");
      const before = { url: url(), id: current.current };
      setTimeout(() => {
        if (url() === before.url && current.current === before.id) close.current();
      }, 0);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("click", onClick, true);
    };
  }, [open]);
}

export function TaskDrawer({
  api,
  company,
  taskId,
  onClose,
  onOpenTask,
}: {
  api: Api;
  company: CompanyAccess;
  taskId: string | null;
  onClose: () => void;
  onOpenTask: (id: string) => void;
}) {
  const width = useDrawerWidth(taskId !== null);
  useClickOutside(taskId, onClose);
  const navigate = useNavigate();
  const fullPage = (id: string) =>
    void navigate({
      to: "/c/$slug/work/t/$taskId",
      params: { slug: company.slug, taskId: id },
      search: keepFocus,
    } as never);
  return (
    <Drawer
      open={taskId !== null}
      onOpenChange={(open) => (open ? null : onClose())}
      width={`${width}px`}
      // A click outside is ours (`useClickOutside`): it spares cards and unsent messages.
      disablePointerDismissal
    >
      {taskId ? (
        <TaskView
          api={api}
          company={company}
          taskId={taskId}
          host="drawer"
          initialWidth={width}
          onClose={onClose}
          onOpenTask={onOpenTask}
          onFullPage={() => fullPage(taskId)}
        />
      ) : null}
    </Drawer>
  );
}
